import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, Col, Collapse, Container, Form, Row } from "reactstrap"
import { connect } from "react-redux"
import { useLocation, withRouter } from "react-router-dom"
import { withTranslation } from "react-i18next"

import { registerLocale, setDefaultLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "assets/scss/datatables.scss"
import ptBR from "date-fns/locale/pt-BR"
import moment from "moment"
import "moment/locale/pt-br"

import Breadcrumbs from "components/Common/Breadcrumb"
import AlertaSemVinculo from "../../components/Common/alerta-sem-vinculo"
import FormFilterCompany from "../../components/Common/form-filter-company"
import FormFilterPeriod from "../../components/Common/form-filter-period"
import FormFilterSelect from "../../components/Common/form-filter-select"
import FormFilterText from "../../components/Common/form-filter-text"
import FormFilterNumber from "../../components/Common/form-filter-number"
import ConsultaVendasDetalheModal from "./consulta-vendas-detalhe-modal"
import ConsultaVendasResumo from "./consulta-vendas-resumo"
import ConsultaVendasTable from "./consulta-vendas-table"
import { Filters } from "../../common/data/definitions"
import { getCompanies, getCompaniesValuesByOptions, getCompanyOption, getDatasPeloPeriodo } from "../../helpers/utils"
import usePermission from "../../helpers/permission/usePermission"
import { navegarBreadCrumb } from "../../store/navigator/actions"

moment.locale("pt-br")
registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const SITUACAO_PADRAO = "A"
const PERIODO_PADRAO = "today"

const ConsultaVendasLista = props => {
  const location = useLocation()

  const [filters, setFilters] = useState(null)
  const [consultaInicialFeita, setConsultaInicialFeita] = useState(false)

  const [companies, setCompanies] = useState([])
  const [companiesOptions, setCompaniesOptions] = useState([])

  const [period, setPeriod] = useState(PERIODO_PADRAO)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())

  const [situacao, setSituacao] = useState(SITUACAO_PADRAO)
  const [tipoVenda, setTipoVenda] = useState("")
  const [canalVenda, setCanalVenda] = useState("")
  const [buscaRapida, setBuscaRapida] = useState("")

  /* O grid não é desmontado ao abrir o detalhamento, então os filtros da busca são preservados. */
  const [vendaSelecionada, setVendaSelecionada] = useState(null)
  const podeVisualizar = usePermission("consultaVendas_visualizar")

  const [maisFiltrosAberto, setMaisFiltrosAberto] = useState(false)
  const [loja, setLoja] = useState()
  const [caixa, setCaixa] = useState()
  const [operadorCaixa, setOperadorCaixa] = useState()
  const [vendedor, setVendedor] = useState()
  const [valorMinimo, setValorMinimo] = useState()
  const [valorMaximo, setValorMaximo] = useState()

  const isInforangra = Boolean(props.user?.usuario_info)
  const isInforangraAdmin = Boolean(props.user?.usuario_info && props.user?.usuario_info?.perfil?.administrador)
  const possuiVinculo = (props.user?.vinculos || []).some(vinculo => ["A", "T"].includes(vinculo.vinculo_status))

  const periodos = [
    { label: props.t("consultavendas.periodo.hoje"), value: "today" },
    { label: props.t("consultavendas.periodo.ontem"), value: "yesterday" },
    { label: props.t("consultavendas.periodo.ultimos7"), value: "lastDays" },
    { label: props.t("consultavendas.periodo.estemes"), value: "thisMonth" },
    { label: props.t("consultavendas.periodo.mesanterior"), value: "lastMonth" },
    { label: props.t("consultavendas.periodo.personalizado"), value: "period" }
  ]

  useEffect(() => {
    props.navegarBreadCrumb({ title: "menu.label.smd.sales.query", link: location.pathname })
  }, [])

  /* A tela abre com as empresas que o usuário já tinha escolhido no dashboard. */
  useEffect(() => {
    if (props.dashboardFilters?.companiesSelected) {
      setCompanies(props.dashboardFilters.companiesSelected)
    }
  }, [props.dashboardFilters])

  useEffect(() => {
    if (isInforangraAdmin && Array.isArray(props.empresas)) {
      setCompaniesOptions(props.empresas.map(empresa => getCompanyOption(empresa)))
    } else {
      setCompaniesOptions(getCompanies(props.user))
    }
    return () => {
      setCompaniesOptions([])
    }
  }, [props.user, props.empresas])

  /* Usuário com uma única empresa não precisa escolher: ela já entra selecionada. */
  useEffect(() => {
    if (companiesOptions.length === 1 && companies.length === 0) {
      setCompanies(companiesOptions)
    }
  }, [companiesOptions])

  /*
   * Primeira consulta assim que o contexto de empresa está resolvido; daí em diante quem dispara a
   * consulta é o botão Buscar. O usuário Inforangra administrador enxerga qualquer empresa, então
   * para ele a consulta só acontece depois que houver empresa escolhida — seja a que veio do
   * dashboard, seja a que ele selecionar na tela.
   */
  useEffect(() => {
    if (consultaInicialFeita || companiesOptions.length === 0) {
      return
    }
    if (isInforangraAdmin && companies.length === 0) {
      return
    }
    setFilters(montarFiltros())
    setConsultaInicialFeita(true)
  }, [companiesOptions, companies])

  const montarFiltros = () => {
    const filtrosMontados = []

    /*
     * O usuário Inforangra administrador enxerga qualquer empresa, então precisa escolher uma:
     * sem escolha não consulta nada, como nas demais listagens. Os demais só enxergam as empresas
     * do próprio vínculo, e sem escolha consultam todas elas.
     * A tela não manda ignoreCompany: ele desliga as duas regras no applyEmpresaFilter e faria o
     * administrador varrer o sistema inteiro.
     */
    const empresasConsultadas = (companies.length || isInforangraAdmin) ? companies : companiesOptions
    filtrosMontados.push({ id: "idEmpresa", value: getCompaniesValuesByOptions(empresasConsultadas) })

    const datasPeloPeriodo = getDatasPeloPeriodo(period, startDate, endDate)
    if (datasPeloPeriodo) {
      filtrosMontados.push({ id: "Data", value: datasPeloPeriodo.inicio, condition: Filters.greaterThanOrEqualTo })
      filtrosMontados.push({ id: "Data", value: datasPeloPeriodo.fim, condition: Filters.lessThanOrEqualTo })
    }

    if (situacao) filtrosMontados.push({ id: "Status", value: situacao })
    if (tipoVenda) filtrosMontados.push({ id: "Tipo", value: tipoVenda })
    if (canalVenda) filtrosMontados.push({ id: "canalVenda", value: canalVenda })
    if (buscaRapida.trim()) filtrosMontados.push({ id: "buscaRapida", value: buscaRapida.trim() })

    if (loja !== undefined) filtrosMontados.push({ id: "Loja", value: loja })
    if (caixa !== undefined) filtrosMontados.push({ id: "Caixa", value: caixa })
    if (operadorCaixa !== undefined) filtrosMontados.push({ id: "Func_Caixa", value: operadorCaixa })
    if (vendedor !== undefined) filtrosMontados.push({ id: "Func_Venda", value: vendedor })
    if (valorMinimo !== undefined) filtrosMontados.push({ id: "valorMinimo", value: valorMinimo })
    if (valorMaximo !== undefined) filtrosMontados.push({ id: "valorMaximo", value: valorMaximo })

    return filtrosMontados
  }

  /* Cada busca gera um array novo: é a mudança de referência que dispara o grid e o resumo. */
  const pesquisar = () => {
    setFilters(montarFiltros())
  }

  const onPickStartDate = date => {
    setStartDate(date)
    if (!endDate || moment(date).isAfter(endDate)) {
      setEndDate(date)
    }
  }

  if (!isInforangra && !possuiVinculo) {
    return (<AlertaSemVinculo />)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="menu.label.smd.sales.query" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-3">{ props.t("consultavendas.titulo") }</h4>

                  <Form onSubmit={ e => { e.preventDefault(); pesquisar() } }>
                    <Row>
                      <div className="col-md-3 col-sm-12">
                        <FormFilterCompany
                          attribute="companies"
                          value={ companies }
                          description={ props.t("consultavendas.filtro.empresa") }
                          setValue={ value => setCompanies(value) }
                          options={ companiesOptions }
                          multiple={ true }
                          hasSelectAll={ !isInforangraAdmin }
                        />
                      </div>

                      <FormFilterPeriod
                        periodValue={ period }
                        periodDescription={ props.t("consultavendas.filtro.periodo") }
                        periodChangeHandeler={ value => setPeriod(value) }
                        options={ periodos }
                        startDateValue={ startDate }
                        startDateChangeHandeler={ date => onPickStartDate(date) }
                        endDateValue={ endDate }
                        endDateChangeHandeler={ date => setEndDate(date) }
                        t={ props.t }
                      />

                      <div className="col-xl col-sm-6">
                        <FormFilterSelect
                          attribute="situacao"
                          value={ situacao }
                          description={ props.t("consultavendas.filtro.situacao") }
                          changeHandeler={ value => setSituacao(value) }
                          options={ [
                            { value: "A", label: props.t("consultavendas.situacao.ativa") },
                            { value: "P", label: props.t("consultavendas.situacao.processamento") },
                            { value: "C", label: props.t("consultavendas.situacao.cancelada") },
                            { value: "", label: props.t("consultavendas.situacao.todas") }
                          ] }
                        />
                      </div>

                      <div className="col-xl col-sm-6">
                        <FormFilterSelect
                          attribute="tipoVenda"
                          value={ tipoVenda }
                          description={ props.t("consultavendas.filtro.tipo") }
                          changeHandeler={ value => setTipoVenda(value) }
                          options={ [
                            { value: "", label: props.t("consultavendas.tipo.todos"), defaultValue: true },
                            { value: "M", label: props.t("consultavendas.tipo.multiplo") },
                            { value: "C", label: props.t("consultavendas.tipo.cartao") },
                            { value: "H", label: props.t("consultavendas.tipo.cheque") },
                            { value: "P", label: props.t("consultavendas.tipo.crediario") }
                          ] }
                        />
                      </div>

                      <div className="col-xl col-sm-6">
                        <FormFilterSelect
                          attribute="canalVenda"
                          value={ canalVenda }
                          description={ props.t("consultavendas.filtro.canal") }
                          changeHandeler={ value => setCanalVenda(value) }
                          options={ [
                            { value: "", label: props.t("consultavendas.canal.todos"), defaultValue: true },
                            { value: "1", label: props.t("consultavendas.canal.pdv") },
                            { value: "2", label: props.t("consultavendas.canal.pedido") },
                            { value: "3", label: props.t("consultavendas.canal.os") },
                            { value: "4", label: props.t("consultavendas.canal.ordemcarregamento") },
                            { value: "5", label: props.t("consultavendas.canal.entregapdv") },
                            { value: "6", label: props.t("consultavendas.canal.entregaapp") },
                            { value: "7", label: props.t("consultavendas.canal.entregaweb") },
                            { value: "8", label: props.t("consultavendas.canal.entregainfinitifood") }
                          ] }
                        />
                      </div>
                    </Row>

                    <Row>
                      <div className="col-md-4 col-sm-12">
                        <FormFilterText
                          attribute="buscaRapida"
                          value={ buscaRapida }
                          description={ props.t("consultavendas.filtro.busca") }
                          changeHandeler={ value => setBuscaRapida(value) }
                          maxLength={ 60 }
                        />
                        <small className="text-muted">{ props.t("consultavendas.filtro.busca.ajuda") }</small>
                      </div>

                      <div className="col col-auto align-self-start mt-md-4 pt-1 row">
                        <div className="mt-3 col col-auto">
                          <Button color="primary" className="w-md" onClick={ () => pesquisar() }>
                            { props.t("consultavendas.filtro.buscar") }
                          </Button>
                        </div>
                        <div className="mt-3 col col-auto">
                          <Button color="light" onClick={ () => setMaisFiltrosAberto(!maisFiltrosAberto) }>
                            { props.t("consultavendas.filtro.mais") }
                            <i className={ maisFiltrosAberto ? "mdi mdi-chevron-up ms-1" : "mdi mdi-chevron-down ms-1" } />
                          </Button>
                        </div>
                      </div>
                    </Row>

                    <Collapse isOpen={ maisFiltrosAberto }>
                      <Row>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="loja"
                            value={ loja }
                            description={ props.t("consultavendas.filtro.loja") }
                            changeHandeler={ value => setLoja(value) }
                            allowNegative={ false }
                          />
                        </div>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="caixa"
                            value={ caixa }
                            description={ props.t("consultavendas.filtro.caixa") }
                            changeHandeler={ value => setCaixa(value) }
                            allowNegative={ false }
                          />
                        </div>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="operadorCaixa"
                            value={ operadorCaixa }
                            description={ props.t("consultavendas.filtro.operador") }
                            changeHandeler={ value => setOperadorCaixa(value) }
                            allowNegative={ false }
                          />
                        </div>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="vendedor"
                            value={ vendedor }
                            description={ props.t("consultavendas.filtro.vendedor") }
                            changeHandeler={ value => setVendedor(value) }
                            allowNegative={ false }
                          />
                        </div>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="valorMinimo"
                            value={ valorMinimo }
                            description={ props.t("consultavendas.filtro.valorminimo") }
                            changeHandeler={ value => setValorMinimo(value) }
                            decimalScale={ 2 }
                            allowNegative={ false }
                          />
                        </div>
                        <div className="col-xl col-sm-6">
                          <FormFilterNumber
                            attribute="valorMaximo"
                            value={ valorMaximo }
                            description={ props.t("consultavendas.filtro.valormaximo") }
                            changeHandeler={ value => setValorMaximo(value) }
                            decimalScale={ 2 }
                            allowNegative={ false }
                          />
                        </div>
                      </Row>
                    </Collapse>
                  </Form>
                </CardBody>
              </Card>

              { filters && <ConsultaVendasResumo filters={ filters } /> }

              { filters && <Card>
                <CardBody>
                  <ConsultaVendasTable filters={ filters }
                                       podeVisualizar={ podeVisualizar }
                                       onVisualizar={ venda => setVendaSelecionada(venda) } />
                </CardBody>
              </Card> }

              <ConsultaVendasDetalheModal isOpen={ Boolean(vendaSelecionada) }
                                          venda={ vendaSelecionada }
                                          onClose={ () => setVendaSelecionada(null) } />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

ConsultaVendasLista.propTypes = {
  user: PropTypes.any,
  empresas: PropTypes.any,
  dashboardFilters: PropTypes.any,
  navegarBreadCrumb: PropTypes.func,
  t: PropTypes.func
}

const mapStatetoProps = state => {
  const { empresas } = state.Profile
  const { user, dashboardFilters } = state.Login
  return { user, empresas, dashboardFilters }
}

export default withTranslation()(connect(mapStatetoProps, {
  navegarBreadCrumb
})(withRouter(ConsultaVendasLista)))
